//inspection view 
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useParams } from "react-router-dom";
import { MASTER_API, PURCHASE_API } from "../../apiConfig";
import sbeeLogo from "../../assets/sbeelogo.png";
import html2pdf from "html2pdf.js";

const QUALITY_INSP_API = `${PURCHASE_API}/quality-inspection`;
const CUSTOMER_INSP_API = `${PURCHASE_API}/customer-inspection`;

const getLoggedInUser = () => {
  try {
    const raw = localStorage.getItem("user") || "";
    if (raw) {
      const parsed = JSON.parse(raw);
      // Prefer the actual user id (e.g. "maithili") so this stays
      // consistent with Purchase Request / Goods Receipt / Purchase Order /
      // Audit Calendar, which all store userId rather than a name or email.
      if (parsed.userId) return parsed.userId;
      return parsed.name || parsed.username || parsed.email || "";
    }
  } catch {}
  return localStorage.getItem("userId") || localStorage.getItem("username") || localStorage.getItem("name") || "";
};

const InspectionReportView = forwardRef(({ inspectionNo: propInspectionNo, stockId, source = "quality", testType }, ref) => {
  const params = useParams();
  const inspectionNo = decodeURIComponent(propInspectionNo || params.inspectionNo);
  const [reportData, setReportData] = useState([]);
  const [stockInfo, setStockInfo] = useState({});
  // Sample column labels for this batch — set via the "Sample No" popup in
  // the Quality Inspection modal, defaults to 3 / Sample. 1-3 when a batch
  // has no custom config yet.
  const [sampleLabels, setSampleLabels] = useState(["Sample. 1", "Sample. 2", "Sample. 3"]);
  const [testedBy, setTestedBy] = useState("");
  const [witnessedBy, setWitnessedBy] = useState("");
  const [witnessedDesignation, setWitnessedDesignation] = useState("");
  const reportRef = useRef();

  // fetch() doesn't go through the app's global axios interceptor (that
  // one only patches axios itself, in apiConfig.js), so every fetch() call
  // below needs the Authorization header attached manually or the
  // guarded quality-inspection routes (stock-header, batch-actuals) 401
  // silently — fetch() doesn't throw on non-2xx, so the error body was
  // being treated as real data, leaving every header field blank except
  // Date of Testing (which is hardcoded via new Date(), not an API call).
  const getAuthHeaders = () => {
    let token = localStorage.getItem("token");
    if (!token) {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        token = user?.token || null;
      } catch (e) {
        token = null;
      }
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    loadReport();
  }, [inspectionNo, stockId, source, testType]);

  const loadReport = async () => {
    try {
      const actualInspectionNo = inspectionNo.replace("_InspectionReport", "");
      const encodedNo = actualInspectionNo.includes("__")
        ? actualInspectionNo
        : actualInspectionNo.replace(/\//g, "__");

      if (stockId) {
        const actualsBase = source === "customer" ? CUSTOMER_INSP_API : QUALITY_INSP_API;
        const [avRes, stockRes] = await Promise.all([
          fetch(`${actualsBase}/batch-actuals/${stockId}${testType ? `?testType=${encodeURIComponent(testType)}` : ""}`, { headers: getAuthHeaders() }),
          fetch(`${QUALITY_INSP_API}/stock-header/${stockId}`, { headers: getAuthHeaders() }),
        ]);
        const avPayload = await avRes.json();
        const stockData = await stockRes.json();

        if (!avRes.ok || !stockRes.ok) {
          console.error(
            "InspectionReportView: batch-actuals/stock-header request failed",
            { avStatus: avRes.status, avPayload, stockStatus: stockRes.status, stockData }
          );
        }

        setStockInfo(stockData || {});

        const productCode = stockData.product_code || "";

        const detailUrl = productCode
          ? `${MASTER_API}/inspection/detail/${encodedNo}?productCode=${encodeURIComponent(productCode)}${testType ? `&testType=${encodeURIComponent(testType)}` : ""}`
          : `${MASTER_API}/inspection/detail/${encodedNo}`;
        const res = await fetch(detailUrl, { headers: getAuthHeaders() });
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];

        // ── DEBUG: log first row to see what fields/values come back ──
        if (rows.length > 0) {
          console.log("📋 InspectionReportView — first detail row:", rows[0]);
          console.log("📋 Distinct test_type values:", [...new Set(rows.map(r => r.test_type))]);
        }

        const avData = Array.isArray(avPayload)
          ? avPayload
          : (avPayload.actuals || avPayload.data || avPayload.rows || []);

        const labels = Array.isArray(avPayload.sample_labels) && avPayload.sample_labels.length > 0
          ? avPayload.sample_labels
          : ["Sample. 1", "Sample. 2", "Sample. 3"];
        setSampleLabels(labels);
        setTestedBy(avPayload.tested_by || "");
        setWitnessedBy(avPayload.witnessed_by || "");
        setWitnessedDesignation(avPayload.witnessed_designation || "");

        // ── DEBUG: log actuals ──
        console.log("📋 avData length:", avData.length, "sample:", avData[0]);

        const avMap = {};
        avData.forEach((av) => {
          const key = av.characteristic_id ?? av.id ?? av.parameter_id;
          const values = Array.isArray(av.sample_values) && av.sample_values.length > 0
            ? av.sample_values
            : [av.sample1 || "", av.sample2 || "", av.sample3 || ""];
          avMap[key] = {
            sample_values: values,
            status: av.manual_result || av.status || "",
            remarks: av.remarks || "",
          };
        });

        const merged = rows.map((r) => {
          const rowId = r.id ?? r.characteristic_id ?? r.parameter_id;
          const actuals = avMap[rowId];
          return actuals
            ? { ...r, sample_values: actuals.sample_values, status: actuals.status, remarks: actuals.remarks }
            : { ...r, sample_values: labels.map(() => ""), status: "", remarks: "" };
        });

        console.log("📋 merged rows count:", merged.length);

        // Only include characteristics that actually have a Standard Value
        // configured — same rule as the Edit modal in Quality.js, applied
        // to every test type (not just Routine Test).
        // Only parameters that were ticked (= saved actuals exist) in the
        // inspection modal appear on the report.
        const withStdValue = merged.filter(
          (r) => r.standard_value !== null && r.standard_value !== undefined && String(r.standard_value).trim() !== ""
            && avMap[r.id ?? r.characteristic_id ?? r.parameter_id]
        );
        setReportData(withStdValue);

      } else {
        const res = await fetch(`${MASTER_API}/inspection/detail/${encodedNo}`, { headers: getAuthHeaders() });
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        console.log("📋 No stockId — detail rows:", rows.length, rows[0]);
        const withStdValue = rows.filter(
          (r) => r.standard_value !== null && r.standard_value !== undefined && String(r.standard_value).trim() !== ""
        );
        setReportData(withStdValue);
      }
    } catch (err) {
      console.error("InspectionReportView loadReport error:", err);
    }
  };

  const handleDownload = async () => {
    const original = reportRef.current;
    if (!original) return;

    const clone = original.cloneNode(true);
    clone.style.position = "fixed";
    clone.style.top = "0";
    clone.style.left = "0";
    clone.style.zIndex = "-1";
    clone.style.margin = "0";
    clone.style.opacity = "1";
    clone.style.display = "block";
    clone.style.visibility = "visible";
    clone.style.background = "#fff";

    const wrapper = document.createElement("div");
    wrapper.style.position = "fixed";
    wrapper.style.top = "0";
    wrapper.style.left = "0";
    wrapper.style.width = "1000px";
    wrapper.style.background = "#fff";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    // Convert logo img(s) inside the clone to base64 data URIs so there's
    // zero chance of a cross-origin/canvas-tainting issue during capture.
    const imgs = Array.from(clone.querySelectorAll("img"));
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            const tempImg = new Image();
            tempImg.crossOrigin = "anonymous";
            tempImg.onload = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = tempImg.naturalWidth || tempImg.width;
                canvas.height = tempImg.naturalHeight || tempImg.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(tempImg, 0, 0);
                img.src = canvas.toDataURL("image/png");
              } catch (e) {
                // If conversion fails (e.g. truly cross-origin), leave original src
              }
              resolve();
            };
            tempImg.onerror = () => resolve();
            tempImg.src = img.src;
          })
      )
    );

    await new Promise((resolve) => setTimeout(resolve, 150));

    const opt = {
      margin: 10,
      filename: `${inspectionNo}_InspectionReport.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
      },
      jsPDF: { unit: "mm", format: "a3", orientation: "landscape" },
    };

    try {
      await html2pdf().set(opt).from(clone).save();
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  useImperativeHandle(ref, () => ({ handleDownload }));

  const groupByProcess = (data) => {
    if (!Array.isArray(data)) return {};
    return data.reduce((acc, row) => {
      const process = row.process_name || "General";
      if (!acc[process]) acc[process] = [];
      acc[process].push(row);
      return acc;
    }, {});
  };

  // Single combined table — no more Electrical / Dimensional split.
  // Test Type is shown per-row instead (added right after Specified Value).
  const allTestsGrouped = groupByProcess(reportData);

  const header = reportData.length > 0 ? reportData[0] : {};

  const th = {
    border: "1px solid black",
    padding: "4px",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "12px",
  };
  const td = { border: "1px solid black", padding: "4px", fontSize: "11px" };
  const tdLabel = { fontWeight: "bold" };
  const loggedInUser = getLoggedInUser();

  // Reusable test table renderer
  const renderTestTable = (grouped) => (
    <tbody>
      {Object.entries(grouped).map(([process, rows]) => (
        <React.Fragment key={process}>
          <tr>
            <td colSpan={6 + sampleLabels.length} style={{ border: "1px solid black", padding: "8px", fontWeight: "bold", background: "#f5f5f5" }}>
              {process.toUpperCase()}
            </td>
          </tr>
          {rows.map((row, index) => {
            const statusRaw = (row.status || "").toString().trim();
            const statusLower = statusRaw.toLowerCase();
            const statusColor = statusLower === "pass" ? "#15803d" : statusLower === "fail" ? "#b91c1c" : "#334155";
            const values = row.sample_values || [];
            // Sample Requirement = "Yes" is the only case that uses Sample
            // 2+ — same rule as the Edit/View modals in Quality.js. Every
            // other row is locked to Sample 1, so later sample columns are
            // left blank here even if stale values exist for them.
            const singleSample = row.sample_requirement !== "Yes";
            return (
              <tr key={index}>
                <td style={{ ...td, paddingLeft: "25px" }}>{row.characteristic}</td>
                <td style={td}>{row.standard_value}</td>
                <td style={td}>{row.test_type || ""}</td>
                <td style={td}>{row.uom}</td>
                {singleSample ? (
                  <td colSpan={sampleLabels.length} style={{ ...td, textAlign: "center" }}>{values[0] || ""}</td>
                ) : (
                  sampleLabels.map((_, si) => (
                    <td key={si} style={{ ...td, textAlign: "center" }}>{values[si] || ""}</td>
                  ))
                )}
                <td style={{ ...td, textAlign: "center", fontWeight: "bold", color: statusColor }}>
                  {statusRaw ? statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1) : ""}
                </td>
                <td style={td}>{row.remarks || ""}</td>
              </tr>
            );
          })}
        </React.Fragment>
      ))}
    </tbody>
  );

  // Column widths: 4 fixed base columns + 2 fixed trailing columns share
  // 50% of the table width; the remaining 50% is split evenly across
  // however many sample columns are configured for this batch.
  const baseColWidths = [26, 13, 11, 6];
  const trailingColWidths = [9, 17];
  const sampleColWidth = sampleLabels.length > 0 ? (100 - baseColWidths.reduce((a, b) => a + b, 0) - trailingColWidths.reduce((a, b) => a + b, 0)) / sampleLabels.length : 0;

  const testTableCols = (
    <colgroup>
      {baseColWidths.map((w, i) => <col key={`b${i}`} style={{ width: `${w}%` }} />)}
      {sampleLabels.map((_, i) => <col key={`s${i}`} style={{ width: `${sampleColWidth}%` }} />)}
      {trailingColWidths.map((w, i) => <col key={`t${i}`} style={{ width: `${w}%` }} />)}
    </colgroup>
  );

  const testTableHead = (
    <thead>
      <tr>
        <th rowSpan="2" style={th}>PARTICULARS</th>
        <th rowSpan="2" style={th}>SPECIFIED VALUE</th>
        <th rowSpan="2" style={th}>TEST TYPE</th>
        <th rowSpan="2" style={th}>UOM</th>
        <th colSpan={sampleLabels.length} style={th}>TEST RESULTS</th>
        <th rowSpan="2" style={th}>STATUS</th>
        <th rowSpan="2" style={th}>REMARKS</th>
      </tr>
      <tr>
        {sampleLabels.map((label, i) => (
          <th key={i} style={th}>{String(label).toUpperCase()}</th>
        ))}
      </tr>
    </thead>
  );

  return (
    <div>
      <div
        ref={reportRef}
        className="inspection-report-content"
        style={{
          width: "1000px",
          margin: "20px auto",
          border: "1px solid black",
          background: "#fff",
          fontFamily: "Calibri",
        }}
      >
        {/* Company Header */}
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid black", padding: "15px" }}>
          <img src={sbeeLogo} alt="SBEE" style={{ width: "150px", height: "auto" }} />
          <div style={{ flex: 1, textAlign: "center", fontSize: "28px", fontWeight: "bold", fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif" }}>
            SBEE CABLES (INDIA) LIMITED
          </div>
        </div>

        {/* Report Title */}
        <div style={{ textAlign: "center", fontSize: "22px", fontWeight: "bold", borderTop: "1px solid black", borderBottom: "1px solid black" }}>
          {testType || header.inspection_type || "INSPECTION REPORT"}
        </div>

        {/* Header Section */}
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "40%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "25%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td style={td}><span style={tdLabel}>Sales Order</span> : {stockInfo.sales_order || ""}</td>
              <td style={td}><span style={tdLabel}>PRD No</span> : {stockInfo.prd_no || ""}</td>
              <td style={td}><span style={tdLabel}>Construction</span> : {stockInfo.construction || ""}</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Product Code</span> : {stockInfo.product_code || ""}</td>
              <td style={td}><span style={tdLabel}>Customer Name</span> : {stockInfo.customer_name || ""}</td>
              <td style={td}><span style={tdLabel}>Drum Type</span> : {stockInfo.drum_type || ""}</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Product Description</span> : {stockInfo.product_description || ""}</td>
              <td style={td}><span style={tdLabel}>Cable Type</span> : {stockInfo.cable_type || ""}</td>
              <td style={td}><span style={tdLabel}>Wooden / Steel</span> : {stockInfo.drum_material || ""}</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Voltage Grade</span> : {stockInfo.voltage || ""}</td>
              <td style={td}><span style={tdLabel}>Date of Testing</span> : {new Date().toLocaleDateString("en-GB")}</td>
              <td style={td}><span style={tdLabel}>Top (M)</span> : {stockInfo.top_m ?? ""}</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Specification</span> : {stockInfo.reference_of_standard || ""}</td>
              <td style={td}><span style={tdLabel}>Batch No</span> : {stockInfo.batch_number || ""}</td>
              <td style={td}><span style={tdLabel}>Bottom (M)</span> : {stockInfo.bottom_m ?? ""}</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Temp</span> : {stockInfo.temp || ""}</td>
              <td style={td}><span style={tdLabel}>Drum No</span> : {stockInfo.drum_no || ""}</td>
              <td style={td}><span style={tdLabel}>Length (M)</span> : {stockInfo.length_m ?? ""}</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Tested By</span> : {testedBy}</td>
              {(testType || header.inspection_type || "").toLowerCase() === "customer inspection" && (
                <>
                  <td style={td}><span style={tdLabel}>Witnessed By</span> : {witnessedBy}</td>
                  <td style={td}><span style={tdLabel}>Designation</span> : {witnessedDesignation}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>

        {/* TEST RESULTS — single combined table, Test Type shown per row */}
        <div style={{ textAlign: "center", fontWeight: "bold", border: "1px solid black", padding: "8px" }}>
          TEST RESULTS
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          {testTableCols}
          {testTableHead}
          {renderTestTable(allTestsGrouped)}
        </table>

        {/* Footer */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
          <tbody>
            <tr>
              <td style={{ ...td, fontWeight: "bold" }}>CHECKED BY :</td>
              <td style={{ ...td, fontWeight: "bold" }}>APPROVED BY :</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Name</span> : {loggedInUser}</td>
              <td style={td}><span style={tdLabel}>Name</span> :</td>
            </tr>
            <tr>
              <td style={td}><span style={tdLabel}>Designation</span> :</td>
              <td style={td}><span style={tdLabel}>Designation</span> :</td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", padding: "15px" }}>
          <div><b>Format No: {inspectionNo}</b></div>
          <div><b>Rev.No & Date: 02 & 10.03.2026</b></div>
        </div>
      </div>
    </div>
  );
});

export default InspectionReportView;